import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Youtube } from "lucide-react";
import { ASSETS, MATHA } from "@/lib/constants";
import { useLang } from "@/i18n";
import MandalaDivider from "@/components/MandalaDivider";

const QUICK_DEF = [
  { to: "/about", key: "about" },
  { to: "/parampara", key: "parampara" },
  { to: "/idols", key: "idols" },
  { to: "/sacred-places", key: "places" },
  { to: "/events", key: "events" },
  { to: "/sanchara", key: "sanchara" },
  { to: "/contact", key: "contact" },
  // { to: "/daily-events", key: "daily" },
  { to: "/sevas", key: "sevas" },
  { to: "/gallery", key: "gallery" },
];

const POLICY_LINKS = [
  { href: "/assets/PrivacyPolicy_BhimasethuMunivrindaMutt.pdf", label: "Privacy Policy", key: "privacy" },
  { href: "/assets/TermsnConditions_BhimasethuMunivrindaMutt.pdf", label: "Terms & Conditions", key: "terms" },
  { href: "/assets/CancellationPolicy_BhimasethuMunivrindaMutt.pdf", label: "Cancellation & Refund Policy", key: "cancellation" },
];

export default function Footer() {
  const { t } = useLang();
  return (
    <footer data-testid="site-footer" className="relative bg-[#7B1818] text-[#F4EBDB] mt-16">
      <div className="absolute inset-x-0 -top-px h-16 bg-gradient-to-b from-[#FDFBF7] to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
        <MandalaDivider color="#D4AF37" opacity={0.4} />

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mt-14">
          <div>
            <div className="flex items-center gap-3">
              <img
                src={ASSETS.logo}
                alt="Sri Bhimanakatte Matha"
                className="h-14 w-14 rounded-full object-cover ring-1 ring-[#D4AF37]/60"
              />
              <div className="font-display text-[#F4EBDB] text-xl leading-tight">
                {t.matha.name}
              </div>
            </div>
            <p className="mt-5 text-[13px] leading-relaxed text-[#EAD5B0]/85">
              {t.matha.fullTitle}. {t.matha.subTitle}. {t.footer.brandText}
            </p>
          </div>

          <div>
            <div className="eyebrow text-[#D4AF37]">{t.footer.quickLinks}</div>
            <ul className="mt-5 space-y-2.5">
              {QUICK_DEF.map((q) => (
                <li key={q.to}>
                  <Link
                    data-testid={`footer-link-${q.key}`}
                    to={q.to}
                    className="text-[13.5px] text-[#EAD5B0]/90 hover:text-white transition-colors"
                  >
                    {t.footer.links[q.key]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="eyebrow text-[#D4AF37]">{t.footer.reach}</div>
            <ul className="mt-5 space-y-3 text-[13.5px]">
              <li className="flex gap-3">
                <MapPin size={16} className="mt-1 text-[#D4AF37] shrink-0" />
                <span>{t.matha.address}</span>
              </li>
              <li className="flex gap-3">
                <Phone size={16} className="mt-1 text-[#D4AF37] shrink-0" />
                <a href={`tel:${MATHA.phone.replace(/\s/g, "")}`} className="hover:text-white">
                  {MATHA.phone}
                </a>
              </li>
              <li className="flex gap-3">
                <Mail size={16} className="mt-1 text-[#D4AF37] shrink-0" />
                <a href={`mailto:${MATHA.email}`} className="hover:text-white break-all">
                  {MATHA.email}
                </a>
              </li>
              <li className="flex gap-3">
                <Youtube size={16} className="mt-1 text-[#D4AF37] shrink-0" />
                <a href={MATHA.youtube} target="_blank" rel="noreferrer" className="hover:text-white">
                  {t.footer.youtube}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="eyebrow text-[#D4AF37]">{t.footer.bookSevaTitle}</div>
            <p className="mt-5 text-[13.5px] text-[#EAD5B0]/90 leading-relaxed">
              {t.footer.bookSevaText}
            </p>
            <Link
              to="/sevas"
              data-testid="footer-book-seva-btn"
              className="inline-flex items-center mt-5 px-5 py-2.5 rounded-full bg-[#D4AF37] text-[#3A2410] font-semibold text-sm hover:bg-[#e5c04a] transition-colors"
            >
              {t.footer.exploreSevas}
            </Link>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-[#D4AF37]/25 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-[#EAD5B0]/70">
          <div>© {new Date().getFullYear()} {t.matha.name} ({t.matha.aka}). {t.footer.rights}</div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            {POLICY_LINKS.map((link) => (
              <a
                key={link.key}
                data-testid={`footer-policy-${link.key}`}
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="text-[#EAD5B0]/85 hover:text-white transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="tracking-[0.2em] uppercase">ॐ श्री गुरुभ्यो नमः</div>
          </div>
        </div>
      </div>
    </footer>
  );
}
